// ═══════════════════════════════════════════════════════════════════════════════════════════════
// «Tipos y flujos» — el vocabulario y el modelo del recorrido, en un solo lugar
// ═══════════════════════════════════════════════════════════════════════════════════════════════
//
// Lo usan las tres piezas de la pantalla (la portada, el lienzo y el detalle). Vive acá y no
// dentro de un componente porque el día que el back agregue un paso, se cambia una vez.
//
// 🔴 LAS FORMAS ESTÁN MEDIDAS CONTRA EL CÓDIGO QUE LAS GENERA, no contra un documento:
// `kredit-api-MT/modules/integracion-pos/casos/catalogo.js` (tipos, pasos, flujo_por_omision) e
// `infra/repos/flujos.js:todosLosVigentes()` (los comercios con flujo propio). De ahí sale un dato
// que un contrato escrito de memoria no dice: los `steps` de un COMERCIO traen sólo
// `{code, enabled}` —**sin `config`**— mientras que los del flujo por omisión sí lo traen.
//
// 🔴 EL WORKFLOW SE CASA AL TIPO DE SOLICITUD, NO AL COMERCIO. Corrección de Juan, 2026-09-20:
// *«el workflow se casa a la compra web, se casa a la compra de comercio. No se casa a cada uno de
// nuestros comercios»*. La colección `merchant_flows` existe y el catálogo la devuelve, pero esta
// pantalla no trata de eso: trata de los TIPOS. Lo único que queda del comercio acá es un control
// —si algún día la versión de un comercio cambiara un recorrido, se dice en una línea— y hoy no
// cambia ninguno.
//
// 🔴 UN RECORRIDO ES UNA LÍNEA, NO UNA REJILLA. Se muestran los pasos que ese recorrido tiene, y
// nada más: ni huecos, ni columnas fijas, ni marcas de lo que no pasa. Si un tipo tiene dos pasos,
// se ven dos.

export interface PasoCatalogo {
  code: string;
  decide: string;                      // 'TIPO' · 'SIEMPRE' · 'COMERCIO'
  descripcion: string;
  configurable_por_comercio: boolean;
}

export interface PasoWorkflow {
  code: string;
  required: boolean;
  lo_pone: string;                     // 'EL TIPO' · 'EL FLUJO DEL COMERCIO'
}

export interface TipoSolicitud {
  code: string;
  nombre: string;
  origen: string;
  por_omision: boolean;
  workflow: PasoWorkflow[];
  agrega_paso: string | null;
  termina_en: string | null;
}

export interface PasoFlujo {
  code: string;
  enabled: boolean;
  config?: { [clave: string]: any };
}

export interface FlujoPorOmision {
  version_code: string;
  steps: PasoFlujo[];
}

export interface ComercioFlujo {
  merchant: string;
  merchant_code: string;
  nombre: string;
  version: number | null;
  version_code: string | null;
  desde: string | null;
  steps: PasoFlujo[];
}

// ── El vocabulario ─────────────────────────────────────────────────────────────────────────────

/** El orden en que un cliente los vive. Un paso que el back agregue y no esté acá va al final,
 *  con su código formateado: nunca se rompe por vocabulario que todavía no se conoce. */
export const ORDEN_PASOS: string[] = ['SELECCIONAR_PLAN', 'ACEPTAR_PAGARE', 'PAGAR_INICIAL'];

/**
 * 🔴 LO QUE NO SE DIBUJA AUNQUE LLEGUE.
 *
 * `HABILITAR_CUPO` se quitó del canal el 2026-09-19 por decisión de Juan: el cliente siempre firma
 * el pagaré y con eso alcanza. El código del paso sigue existiendo en el back para no romper el
 * contrato de las fotografías viejas (`flow_snapshot`), y algunos comercios todavía lo tienen
 * encendido en su flujo —dato muerto—. Mostrarlo sería contar un paso que hoy no le pasa a nadie.
 */
export const PASOS_OCULTOS: string[] = ['HABILITAR_CUPO'];

const NOMBRES_PASO: { [code: string]: string } = {
  SELECCIONAR_PLAN: 'Elegir el plan',
  ACEPTAR_PAGARE: 'Aceptar los términos y condiciones',
  PAGAR_INICIAL: 'Pagar la inicial',
};

/** El mismo paso, corto. 🔴 Se usa cuando el nombre largo NO entra: se acorta el contenido, nunca
 *  se corta con puntos suspensivos. */
const NOMBRES_CORTOS: { [code: string]: string } = {
  SELECCIONAR_PLAN: 'Plan',
  ACEPTAR_PAGARE: 'Pagaré',
  PAGAR_INICIAL: 'Inicial',
};

const TEXTO_DECIDE: { [decide: string]: string } = {
  TIPO: 'el tipo de solicitud',
  SIEMPRE: 'nadie — corre siempre',
  COMERCIO: 'el flujo del comercio',
};

/** La versión de dos palabras, la que va debajo del nodo en el lienzo. */
const TEXTO_DECIDE_CORTO: { [decide: string]: string } = {
  TIPO: 'lo pone el tipo',
  SIEMPRE: 'corre siempre',
  COMERCIO: 'lo pone el comercio',
};

const SENTIDO_DECIDE: { [decide: string]: string } = {
  TIPO: 'Lo trae el canal por el que entró la venta. El comercio no lo puede apagar.',
  SIEMPRE: 'Corre en todos los recorridos. No lo decide ni el tipo ni el comercio.',
  COMERCIO: 'Lo enciende o lo apaga cada comercio en su propio flujo.',
};

const formatoCodigo = (code: string): string => {
  const limpio = String(code || '').replace(/_/g, ' ').toLowerCase().trim();
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
};

export const nombrePaso = (code: string): string => NOMBRES_PASO[code] || formatoCodigo(code);
export const nombreCortoPaso = (code: string): string => NOMBRES_CORTOS[code] || nombrePaso(code);
export const textoDecide = (decide: string): string => TEXTO_DECIDE[decide] || TEXTO_DECIDE['COMERCIO'];
export const textoDecideCorto = (decide: string): string => TEXTO_DECIDE_CORTO[decide] || TEXTO_DECIDE_CORTO['COMERCIO'];
export const sentidoDecide = (decide: string): string => SENTIDO_DECIDE[decide] || SENTIDO_DECIDE['COMERCIO'];

export const claseDecide = (decide: string): string => {
  if (decide === 'TIPO') return 'kb-badge-marca';
  if (decide === 'SIEMPRE') return 'kb-badge-neutro';
  return 'kb-badge-exito';
};

export const iconoDecide = (decide: string): string => {
  if (decide === 'TIPO') return 'fa-code-branch';
  if (decide === 'SIEMPRE') return 'fa-lock';
  return 'fa-store';
};

export const formatoFecha = (f: any): string => {
  if (!f) return '—';
  const d = new Date(f);
  return isNaN(d.getTime()) ? String(f) : d.toLocaleDateString('es-VE', { year: 'numeric', month: 'short', day: '2-digit' });
};

// ── El modelo del recorrido ────────────────────────────────────────────────────────────────────

/** Un paso que SÍ pasa, en el lugar que ocupa. Los que no pasan no existen en este modelo. */
export interface NodoRecorrido {
  code: string;
  nombre: string;
  corto: string;
  decide: string;
  descripcion: string;
  orden: number;
  /** ¿El cliente lo tiene que hacer sí o sí, o se lo puede saltar? `null` = el catálogo no lo dice
   *  para este paso. Ver `requeridoDelPaso`. */
  requerido: boolean | null;
}

/** Los pasos del catálogo que se muestran, en el orden en que el cliente los vive. */
export const pasosVisibles = (pasos: PasoCatalogo[]): PasoCatalogo[] => {
  const lista = Array.isArray(pasos) ? pasos.filter((p) => !!p && !!p.code && PASOS_OCULTOS.indexOf(p.code) === -1) : [];
  const peso = (code: string): number => {
    const i = ORDEN_PASOS.indexOf(code);
    return i === -1 ? ORDEN_PASOS.length : i;
  };
  return lista.slice().sort((a, b) => peso(a.code) - peso(b.code));
};

const pasoDe = (steps: PasoFlujo[] | null | undefined, code: string): PasoFlujo | undefined =>
  (Array.isArray(steps) ? steps : []).find((s) => s && s.code === code);

const enTipo = (tipo: TipoSolicitud | null, code: string): boolean =>
  (Array.isArray(tipo?.workflow) ? tipo!.workflow : []).some((w) => w && w.code === code);

/**
 * ¿Este recorrido hace este paso?
 *
 * 🔴 El reparto NO sale de un solo lado, y es toda la gracia de la pantalla:
 *   · lo que decide el TIPO lo trae el tipo — el comercio no lo puede tocar;
 *   · lo que corre SIEMPRE corre siempre, diga lo que diga la configuración (`flujo.js` lo empuja
 *     a mano en `pasosQueTocan`, no lo lee de los `steps`);
 *   · lo demás sale del `enabled` del comercio, y si no hay comercio elegido, del recorrido por
 *     omisión que el propio catálogo ya compuso.
 */
export const haceElPaso = (paso: PasoCatalogo, tipo: TipoSolicitud | null, comercio: ComercioFlujo | null): boolean => {
  if (paso.decide === 'TIPO') return enTipo(tipo, paso.code);
  if (paso.decide === 'SIEMPRE') return true;
  if (!comercio) return enTipo(tipo, paso.code);
  return pasoDe(comercio.steps, paso.code)?.enabled === true;
};

/**
 * ¿El paso es obligatorio para el cliente, o se lo puede saltar?
 *
 * El dato LLEGA y hasta hoy no se mostraba en ninguna de las dos pantallas: viaja como `required`
 * dentro del `workflow` de cada tipo, lo arma `integracion-pos/casos/catalogo.js:workflowDe`.
 *
 * Medido contra `dominio/flujo.js` el 2026-09-20: `ACEPTAR_PAGARE` y `PAGAR_INICIAL` viajan
 * `required: true`, y el único opcional que existe —`SELECCIONAR_PLAN`, `required: false`— hoy no
 * lo trae ningún tipo, porque el flujo por omisión lo tiene apagado. Por eso hoy no se ve ni un
 * «opcional» en la consola: el día que un comercio encienda la selección de plan, aparece solo.
 *
 * `null` cuando el catálogo no lo dice para ese paso —uno que enciende el flujo de un comercio y
 * que el tipo no trae no viaja en el `workflow`—: un dato que no llegó no se inventa, se calla.
 */
export const requeridoDelPaso = (tipo: TipoSolicitud | null, code: string): boolean | null => {
  const w = (Array.isArray(tipo?.workflow) ? tipo!.workflow : []).find((x) => x && x.code === code);
  return w ? w.required === true : null;
};

/** El recorrido de un tipo — una línea de pasos, sólo los que pasan. Si hay un comercio elegido,
 *  es el recorrido DE ESE COMERCIO; si no, el de por omisión. */
export const construirRecorrido = (
  tipo: TipoSolicitud | null,
  pasos: PasoCatalogo[],
  comercio: ComercioFlujo | null,
): NodoRecorrido[] => {
  let n = 0;
  return pasosVisibles(pasos)
    .filter((p) => haceElPaso(p, tipo, comercio))
    .map((p) => {
      n += 1;
      return {
        code: p.code,
        nombre: nombrePaso(p.code),
        corto: nombreCortoPaso(p.code),
        decide: p.decide,
        descripcion: p.descripcion || '',
        orden: n,
        requerido: requeridoDelPaso(tipo, p.code),
      };
    });
};

/** En qué se aparta un comercio del recorrido por omisión de ESTE tipo. Vacío = va con el molde. */
export const diferenciaConOmision = (
  tipo: TipoSolicitud | null,
  pasos: PasoCatalogo[],
  comercio: ComercioFlujo | null,
): { agrega: string[]; quita: string[] } => {
  const agrega: string[] = [];
  const quita: string[] = [];
  if (!comercio) return { agrega, quita };
  pasosVisibles(pasos).forEach((p) => {
    const conComercio = haceElPaso(p, tipo, comercio);
    const sinComercio = haceElPaso(p, tipo, null);
    if (conComercio === sinComercio) return;
    (conComercio ? agrega : quita).push(nombrePaso(p.code));
  });
  return { agrega, quita };
};

/** ¿Se aparta en algo? */
export const seAparta = (tipo: TipoSolicitud | null, pasos: PasoCatalogo[], comercio: ComercioFlujo | null): boolean => {
  const d = diferenciaConOmision(tipo, pasos, comercio);
  return d.agrega.length > 0 || d.quita.length > 0;
};

/** La diferencia, escrita. `''` cuando no hay ninguna. */
export const diferenciaEnPalabras = (
  tipo: TipoSolicitud | null,
  pasos: PasoCatalogo[],
  comercio: ComercioFlujo | null,
): string => {
  const d = diferenciaConOmision(tipo, pasos, comercio);
  const partes: string[] = [];
  if (d.agrega.length) partes.push(`agrega ${d.agrega.join(' y ')}`);
  if (d.quita.length) partes.push(`no pide ${d.quita.join(' y ')}`);
  return partes.join(', y ');
};

/** Los comercios con flujo propio que se apartan del recorrido por omisión de este tipo. */
export const comerciosQueSeApartan = (
  tipo: TipoSolicitud | null,
  pasos: PasoCatalogo[],
  comercios: ComercioFlujo[],
): ComercioFlujo[] =>
  (Array.isArray(comercios) ? comercios : []).filter((c) => !!c && seAparta(tipo, pasos, c));

/** Los comercios que se apartan EN ESTE PASO, y de qué lado. */
export const comerciosQueSeApartanEn = (
  tipo: TipoSolicitud | null,
  paso: PasoCatalogo,
  comercios: ComercioFlujo[],
): { nombre: string; version: string; lo_pide: boolean }[] => {
  const sinComercio = haceElPaso(paso, tipo, null);
  return (Array.isArray(comercios) ? comercios : [])
    .filter((c) => !!c && haceElPaso(paso, tipo, c) !== sinComercio)
    .map((c) => ({
      nombre: c.nombre || c.merchant_code || 'Comercio sin nombre',
      version: `${c.version_code || 'V?'} · desde ${formatoFecha(c.desde)}`,
      lo_pide: haceElPaso(paso, tipo, c),
    }));
};

/** El recorrido en palabras: es el `aria-label` del lienzo. Sin esto, para un lector de pantalla
 *  un `<canvas>` es un rectángulo vacío y la pieza central de la pantalla no existe. */
export const recorridoEnPalabras = (
  tipo: TipoSolicitud | null,
  nodos: NodoRecorrido[],
  deQuien: string,
): string => {
  const cabeza = `Recorrido de ${tipo?.nombre || 'este tipo de solicitud'}${deQuien ? ' en ' + deQuien : ''}.`;
  if (!nodos.length) return `${cabeza} Hoy no le pide ningún paso al cliente.`;
  const cuerpo = nodos
    .map((n) => `Paso ${n.orden}, ${n.nombre}, lo pone ${textoDecide(n.decide)}. ${n.descripcion}`)
    .join(' ');
  return `${cabeza} ${nodos.length} ${nodos.length === 1 ? 'paso' : 'pasos'}. ${cuerpo}`;
};

// ── Los tipos, uno debajo del otro ─────────────────────────────────────────────────────────────
//
// 🔴 MEDIDO EL 2026-09-20, y es lo que decide la forma de la pantalla: tres de los cuatro tipos
// tienen UN SOLO paso (firmar el pagaré) y el cuarto tiene dos. Con eso no hay nada que graficar:
// un recorrido de uno o dos puntos se lee mejor escrito que dibujado, y un dibujo de más es peor
// que ninguno. Lo que sí hace falta es la CONCLUSIÓN —que tres hacen lo mismo y uno no— y esa se
// calcula abajo, no se escribe a mano.

export interface CarrilTipo {
  tipo: TipoSolicitud;
  nodos: NodoRecorrido[];
  /** La firma del recorrido: dos tipos con la misma firma hacen exactamente lo mismo. */
  firma: string;
}

export const construirCarriles = (
  tipos: TipoSolicitud[],
  pasos: PasoCatalogo[],
  comercio: ComercioFlujo | null,
): CarrilTipo[] =>
  (Array.isArray(tipos) ? tipos : []).filter((t) => !!t).map((t) => {
    const nodos = construirRecorrido(t, pasos, comercio);
    return { tipo: t, nodos, firma: nodos.map((n) => n.code).join('>') };
  });

/** Los pasos que existen en el catálogo y que hoy no hace nadie. Se dicen, no se dibujan. */
export const pasosQueNadieHace = (carriles: CarrilTipo[], pasos: PasoCatalogo[]): PasoCatalogo[] => {
  const usados: { [code: string]: boolean } = {};
  carriles.forEach((c) => c.nodos.forEach((n) => { usados[n.code] = true; }));
  return pasosVisibles(pasos).filter((p) => usados[p.code] !== true);
};

const y = (lista: string[]): string =>
  lista.length <= 1 ? (lista[0] || '') : lista.slice(0, -1).join(', ') + ' y ' + lista[lista.length - 1];

/**
 * 🔴 LA FRASE QUE LA PANTALLA TIENE QUE DECIR, y que hoy hay que deducir mirando los dibujos:
 * *«los cuatro tipos hacen lo mismo: el cliente firma y listo. El único distinto es el link de
 * pago, que además cobra la inicial en el momento»*.
 *
 * Se CALCULA con los datos del catálogo, no se escribe a mano: el día que un tipo cambie de
 * recorrido, la frase cambia sola. Una conclusión escrita a mano es una que miente sin avisar.
 */
export const conclusion = (carriles: CarrilTipo[]): string => {
  if (!carriles.length) return '';
  const grupos: { firma: string; carriles: CarrilTipo[] }[] = [];
  carriles.forEach((c) => {
    const g = grupos.find((x) => x.firma === c.firma);
    if (g) g.carriles.push(c); else grupos.push({ firma: c.firma, carriles: [c] });
  });
  grupos.sort((a, b) => b.carriles.length - a.carriles.length);

  const mayoria = grupos[0];
  const pasosDe = (c: CarrilTipo): string => c.nodos.length
    ? y(c.nodos.map((n) => n.nombre.toLowerCase()))
    : 'ningún paso';
  const total = carriles.length;

  if (grupos.length === 1) {
    return `Los ${total} tipos hacen exactamente lo mismo: ${pasosDe(mayoria.carriles[0])}.`;
  }

  const resto = grupos.slice(1);
  const cabeza = `${mayoria.carriles.length} de los ${total} tipos hacen lo mismo: ${pasosDe(mayoria.carriles[0])}.`;

  const comunes = mayoria.carriles[0].nodos.map((n) => n.code);
  const cola = resto.map((g) => {
    const nombres = y(g.carriles.map((c) => c.tipo.nombre));
    const suma = g.carriles[0].nodos.filter((n) => comunes.indexOf(n.code) === -1).map((n) => n.nombre.toLowerCase());
    const falta = comunes.filter((code) => !g.carriles[0].nodos.some((n) => n.code === code))
      .map((code) => nombrePaso(code).toLowerCase());
    const partes: string[] = [];
    if (suma.length) partes.push(`agrega ${y(suma)}`);
    if (falta.length) partes.push(`no pide ${y(falta)}`);
    const verbo = g.carriles.length === 1 ? 'es' : 'son';
    return `El${g.carriles.length === 1 ? '' : 'os'} distinto${g.carriles.length === 1 ? '' : 's'} ${verbo} ${nombres}, que ${partes.join(' y ') || 'hace otro recorrido'}.`;
  }).join(' ');

  return `${cabeza} ${cola}`;
};
